import {
  ChangeDetectionStrategy,
  Component,
  computed,
  input,
  signal,
} from '@angular/core';
import { NgTemplateOutlet } from '@angular/common';
import { ActivatedRoute, RouterLink, RouterLinkActive } from '@angular/router';
import { isExternalUrl } from './domain/link.validators';

/**
 * LinkComponent – presentational link component wrapping Angular Router's
 * `routerLink` for internal navigation and `<a href>` for external links.
 *
 * @inputs
 *  - **`route` (required)**    {string}            Internal router path.
 *  - **`external`**            {boolean}           When true, renders an <a href> opening in a new tab.
 *  - **`disabled`**            {boolean}           Prevents navigation and suppresses click events.
 *  - **`rel`**                 {string}            Relationship attribute.
 *  - **`ariaLabel`**           {string}            Aria label attribute.
 *  - **`relativeTo`**          {ActivatedRoute}    Relative route to the current route.
 */
@Component({
  selector: 'ps-link',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [NgTemplateOutlet, RouterLink, RouterLinkActive],
  templateUrl: './link.component.html',
  styleUrl: './link.component.scss',
})
export class LinkComponent {
  public readonly external = input<boolean>();
  public readonly disabled = input<boolean>(false);
  public readonly rel = input<string>('');
  public readonly relativeTo = input<ActivatedRoute>();
  public readonly ariaLabel = input<string>('');
  public readonly contentType = input<'text' | 'image'>('text');
  public readonly route = input.required<string>();

  public readonly target = signal('_blank');

  protected readonly isExternal = computed<boolean>(() => {
    const explicitExternal = this.external();
    if (explicitExternal !== undefined) {
      return explicitExternal;
    }

    const route = this.route();
    return isExternalUrl(route);
  });

  protected readonly tabIndex = computed(() => {
    return this.disabled() ? -1 : 0;
  });

  protected readonly relation = computed<string | null>(() => {
    if (this.rel()) {
      return this.rel();
    }

    if (this.isExternal()) {
      return 'noopener noreferrer';
    }

    return null;
  });

  protected readonly targetAttribute = computed(() => {
    if (this.disabled()) return null;
    return this.isExternal() ? this.target() : null;
  });

  protected readonly dataCy = computed(
    () => `ps-link-${this.isExternal() ? 'external' : 'internal'}`,
  );

  protected readonly linkClass = computed<string>(() => {
    const baseClass = 'ps-link';
    const typeClass = this.isExternal() ? 'external-link' : 'internal-link';
    const disabledClass = this.disabled() ? 'disabled' : '';

    return `${baseClass} ${typeClass} ${disabledClass}`.trim();
  });

  protected readonly href = computed(() => {
    return this.isExternal() && !this.disabled() ? this.route() : null;
  });

  protected readonly routerActive = computed(() => {
    return !this.isExternal() ? 'active' : '';
  });

  protected onClick(event: MouseEvent): void {
    if (this.disabled()) {
      event.preventDefault();
      event.stopPropagation();
    }
  }
}
